/**
 * وضعیت مشترک پرونده‌ی سلامت برای بیمار: یک بار پرسیدن قابلیت، یک بار گرفتن پرونده،
 * و نقشه‌ی «شناسه‌ی پیوست ← مورد پرونده» برای خط زیر پیام‌ها.
 * تا وقتی سرور نگفته قابلیت روشن است، همه‌چیز خالی می‌ماند.
 */

import { useEffect, useSyncExternalStore } from "react";
import { fetchHealthRecord, fetchHealthRecordFeature, HEALTH_RECORD_KINDS } from "../../api/healthRecord";
import { CACHE_KEYS, getCacheSync, setCache } from "../../utils/appDataCache";

let snapshot = { enabled: false, record: null, byAttachment: new Map() };
let featurePromise = null;
let recordPromise = null;
const listeners = new Set();
const DEVICE_KEY = CACHE_KEYS.ticketData("health-record");
let seeded = false;

function emit(next) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((fn) => fn());
}

function indexByAttachment(record) {
  const map = new Map();
  if (!record) return map;
  for (const kind of HEALTH_RECORD_KINDS) {
    for (const item of record.folders[kind] || []) {
      if (item.attachmentId != null) map.set(item.attachmentId, item);
    }
  }
  return map;
}

/** The last answer on the device, in the first render: the rows under messages are there with them. */
function seedFromDevice() {
  if (seeded || snapshot.enabled) return;
  const saved = getCacheSync(DEVICE_KEY, { allowStale: true })?.data;
  if (!saved) return;
  seeded = true;
  snapshot = { enabled: !!saved.enabled, record: saved.record ?? null, byAttachment: indexByAttachment(saved.record) };
}

function saveToDevice() {
  setCache(DEVICE_KEY, { enabled: snapshot.enabled, record: snapshot.record }).catch(() => {});
}

export function ensureHealthRecordFeature() {
  if (!featurePromise) {
    featurePromise = fetchHealthRecordFeature().then((on) => {
      // false also means «the request failed»: offline the device's answer stands
      if (on || typeof navigator === "undefined" || navigator.onLine) {
        emit({ enabled: !!on });
        saveToDevice();
      }
      return on;
    });
  }
  return featurePromise;
}

export function refreshHealthRecord() {
  recordPromise = ensureHealthRecordFeature().then(async (on) => {
    if (!on) return null;
    try {
      const record = await fetchHealthRecord();
      emit({ record, byAttachment: indexByAttachment(record) });
      saveToDevice();
      return record;
    } catch {
      return null;
    }
  });
  return recordPromise;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** @param {{loadRecord?: boolean}} opts */
export function useHealthRecord({ loadRecord = false } = {}) {
  seedFromDevice();
  const state = useSyncExternalStore(subscribe, () => snapshot);
  useEffect(() => {
    ensureHealthRecordFeature();
    if (loadRecord && !recordPromise) refreshHealthRecord();
  }, [loadRecord]);
  return state;
}

/** مورد پرونده برای یک پیام (اولین پیوستی که در پرونده هست). */
export function recordItemForMessage(byAttachment, message) {
  if (!byAttachment || byAttachment.size === 0) return null;
  const list = Array.isArray(message?.attachments) ? message.attachments : [];
  for (const att of list) {
    const id = att?.id ?? att?.attachment_id;
    if (id != null && byAttachment.has(Number(id))) return byAttachment.get(Number(id));
  }
  return null;
}
